<template>
    <div>
        <DCard
            padding
        >
            <DRow
                class="mb-3"
                align-vertical="end"
            >
                <DCol sm="2">
                    <DSelect
                        id="status"
                        v-model="filters.status"
                        :label="__('tools.price_adjustments.status.status')"
                        :options="statusOptionsMapped"
                        :is-valid="isFormFieldValid('status')"
                    />
                </DCol>
                <DCol sm="2">
                    <DInput
                        id="valid_from"
                        v-model="filters.valid_from"
                        :label="__('tools.price_adjustments.table.valid_from')"
                        type="date"
                        :is-valid="isFormFieldValid('valid_from')"
                    />
                </DCol>
                <DCol sm="2">
                    <DInput
                        id="valid_to"
                        v-model="filters.valid_to"
                        :label="__('tools.price_adjustments.table.valid_to')"
                        type="date"
                        :is-valid="isFormFieldValid('valid_to')"
                    />
                </DCol>
                <DCol
                    sm="3"
                    class="d-flex gap-2"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('messages.actions.filter')"
                        @click="filter(priceAdjustments.current_page, filters)"
                    />
                    <DButton
                        id="filter-reset"
                        icon="filter-off"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
            <DTable
                :items="priceAdjustments.data"
                :fields="fields"
            >
                <template #adjustment_rate="{item}">
                    <td>
                        {{ item.adjustment_rate }}%
                    </td>
                </template>
                <template #valid_to="{item}">
                    <td>
                        {{ item.valid_to !== null ? item.valid_to : '' }}
                    </td>
                </template>
                <template #active="{item}">
                    <td>
                        <DTag
                            :color="item.status === 'active' ? 'success' : (item.status === 'planned' ? 'primary' : 'danger')"
                            :label="__('tools.price_adjustments.status.' + item.status)"
                        />
                    </td>
                </template>
                <template #actions="{item}">
                    <td>
                        <div v-if="item.status === 'planned'">
                            <DButton
                                id="edit-price-adjustment"
                                mini
                                icon="pencil"
                                @click="openEditModal(item)"
                            />
                            <DButton
                                id="delete-price-adjustment"
                                mini
                                icon="delete"
                                color="danger"
                                @click="openDeleteModal(item)"
                            />
                        </div>
                    </td>
                </template>
            </DTable>
        </DCard>
        <DPagination
            v-if="priceAdjustments.last_page > 1"
            class="pb-2"
            :active-page="priceAdjustments.current_page"
            :pages="priceAdjustments.last_page"
            align="center"
            @update:activePage="changePage"
        />
        <DFabs>
            <DFab
                id="btn-add-price_adjustment"
                icon="plus"
                :label="__('tools.price_adjustments.buttons.add')"
                @click="createModal = true"
            />
        </DFabs>
        <PriceAdjustmentsCreateModal
            v-if="createModal"
            :show-modal="createModal"
            :price-adjustment="priceAdjustment"
            :errors="errors"
            @close-modal="createModal = false"
            @form-submitted="saveForm"
        />
        <DModal
            v-if="deleteModal"
            :show="deleteModal"
            is-confirm
            is-error
            :title="__('tools.price_adjustments.delete_modal.title')"
            :message="__('tools.price_adjustments.delete_modal.message')"
            @close="deleteModal = false"
            @confirm="deletePriceAdjustment"
        />
    </div>
</template>

<script>
import PriceAdjustmentsCreateModal from '~@/Components/BillingAndAccounting/PriceAdjustments/CreateModal'

export default {
    name: 'PriceAdjustmentsIndex',
    components: {
        PriceAdjustmentsCreateModal

    },
    props: {
        priceAdjustments: {
            type: Object,
            default: () => { return {} }
        },
        appliedFilters: {
            type: Object,
            default: () => { return {} }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        statusOptions: {
            type: Array,
            default: () => { return [] }
        }
    },
    data () {
        return {
            fields: [
                { key: 'adjustment_rate', label: this.__('tools.price_adjustments.table.adjustment_rate') },
                { key: 'valid_from', label: this.__('tools.price_adjustments.table.valid_from') },
                { key: 'valid_to', label: this.__('tools.price_adjustments.table.valid_to') },
                { key: 'active', label: this.__('tools.price_adjustments.table.active') },
                { key: 'actions', label: '' }
            ],
            createModal: false,
            deleteModal: false,
            filters: {
                status: this.appliedFilters.status ?? 'all',
                valid_from: this.appliedFilters.valid_from ?? null,
                valid_to: this.appliedFilters.valid_to ?? null
            },
            priceAdjustment: {}
        }
    },
    computed: {
        statusOptionsMapped () {
            return this.statusOptions.map((option) => {
                return {
                    label: option,
                    value: option
                }
            })
        }
    },
    methods: {
        saveForm (form) {
            if (this.priceAdjustment.id !== undefined) {
                form.put(this.$route('settings.price-adjustments.update', {
                    price_adjustment: this.priceAdjustment.id
                }), {
                    onSuccess: () => {
                        this.createModal = false
                        this.priceAdjustment = {}
                    },
                    onError: () => {
                        this.createModal = true
                    }
                })
            } else {
                form.post(this.$route('settings.price-adjustments.store'), {
                    onSuccess: () => {
                        this.createModal = false
                    },
                    onError: () => {
                        this.createModal = true
                    }
                })
            }
        },
        changePage (page) {
            this.filter(page, this.filters)
        },
        filter (page, filters) {
            const queryParameters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))

            if (page > 1) {
                queryParameters.page = page
            }

            this.$inertia.get(this.$route('settings.price-adjustments.index'), queryParameters)
        },
        resetFilters () {
            this.filter(this.priceAdjustments.current_page, {})
        },
        openDeleteModal (item) {
            this.priceAdjustment = item
            this.deleteModal = true
        },
        openEditModal (item) {
            this.priceAdjustment = item
            this.createModal = true
        },
        deletePriceAdjustment () {
            this.$inertia.delete(this.$route('settings.price-adjustments.destroy', {
                price_adjustment: this.priceAdjustment.id
            }))
        }
    }
}
</script>
